import { useState, useCallback, useContext, createContext, type ReactNode } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

export interface ToastOptions {
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (options: ToastOptions) => void;
  hideToast: (id: string) => void;
}

interface UseToastReturn {
  showToast: (options: ToastOptions) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((options: ToastOptions) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const duration = options.duration ?? 3000;

    const newToast: Toast = {
      id,
      message: options.message,
      type: options.type,
      duration,
    };

    setToasts((prev) => [...prev, newToast]);

    if (duration > 0) {
      setTimeout(() => {
        hideToast(id);
      }, duration);
    }
  }, [hideToast]);

  return (
    <ToastContext.Provider value={{ toasts, showToast, hideToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToastContext(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within ToastProvider');
  }
  return context;
}

export function useToast(): UseToastReturn {
  const { showToast } = useToastContext();

  return {
    showToast,
    success: (message: string, duration?: number) =>
      showToast({ message, type: 'success', ...(duration !== undefined && { duration }) }),
    error: (message: string, duration?: number) =>
      showToast({ message, type: 'error', ...(duration !== undefined && { duration }) }),
    info: (message: string, duration?: number) =>
      showToast({ message, type: 'info', ...(duration !== undefined && { duration }) }),
    warning: (message: string, duration?: number) =>
      showToast({ message, type: 'warning', ...(duration !== undefined && { duration }) }),
  };
}
